import "./map.css";

import { apm } from "@elastic/apm-rum";
import { MapComponent } from "@inovitas/infra3dsdk/dist/types/mm";
import {
  Map,
  MapOutlined,
  ZoomOutMap,
  Texture,
  TextureOutlined,
} from "@mui/icons-material";
import { Box, ClickAwayListener, IconButton, Tooltip } from "@mui/material";
import BaseLayer from "ol/layer/Base";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import basemapDefaultThumbnail from "shared/assets/basemap_default_thumbnail.webp";
import AttributionButton from "./AttributionButton";

export default function MapContent() {
  const mapExpand = defaultStore((state) => state.mapExpand);
  const showStreetMap = defaultStore((state) => state.showStreetMap);
  const changeShowStreetMap = defaultStore(
    (state) => state.changeShowStreetMap
  );
  const showPCLExport = defaultStore((state) => state.showPCLExport);
  const clientConfig = defaultStore((state) => state.clientConfig);

  const { t } = useTranslation();

  const [mapSelectorOpen, setMapSelectorOpen] = useState(false);
  const [overlaySelectorOpen, setOverlaySelectorOpen] = useState(false);

  const [baseLayers, setBaseLayers] = useState<BaseLayer[]>([]);
  const [overlayLayers, setOverlayLayers] = useState<BaseLayer[]>([]);

  const handleOpenBasemapSelector = () => {
    const viewer = defaultStore.getState().viewer;
    if (!viewer) return;
    const mapcomponent: MapComponent = viewer.getComponent("map");
    const tileLayers = mapcomponent.map.getAllBasemapLayers();
    if (tileLayers) {
      setBaseLayers(tileLayers);
      setMapSelectorOpen(true);
    }
  };

  const handleBasemapSelectorClick = (title: string, enable?: boolean) => {
    // "radio button" behaviour
    const viewer = defaultStore.getState().viewer;
    if (!viewer) return;
    const mapcomponent: MapComponent = viewer.getComponent("map");
    const layers = mapcomponent.map.getAllBasemapLayers();

    if (enable === undefined) {
      layers.forEach((layer) => {
        const layerTitle = layer.getProperties().title;
        if (layerTitle === title) {
          mapcomponent.map.enableLayer(title);
        } else {
          mapcomponent.map.disableLayer(layerTitle);
        }
      });
    } else {
      // probably needed for future development
      if (enable) {
        mapcomponent.map.enableLayer(title);
      } else {
        mapcomponent.map.disableLayer(title);
      }
    }

    // to reflect the UI visible/invisible state
    if (layers) {
      setBaseLayers(layers);
      setMapSelectorOpen(false);
    }
  };

  const showBasemapSelectorIcon = () => {
    if (!clientConfig?.map?.base_layers) return false;
    return clientConfig?.map?.base_layers?.length > 1;
  };

  const handleOpenOverlaySelector = () => {
    const viewer = defaultStore.getState().viewer;
    if (!viewer) return;
    const mapcomponent: MapComponent = viewer.getComponent("map");
    const overlayLayers = mapcomponent.map.getAllOverlayLayers();
    if (overlayLayers) {
      setOverlayLayers(overlayLayers);
      setOverlaySelectorOpen(true);
    }
  };

  const handleOverlaySelectorClick = (title: string, enable?: boolean) => {
    // toggle
    const viewer = defaultStore.getState().viewer;
    if (!viewer) return;
    const mapcomponent: MapComponent = viewer.getComponent("map");
    const layers = mapcomponent.map.getAllOverlayLayers();

    const layer = layers.find((layer) => layer.getProperties().title === title);
    if (!layer) return;

    const layerVisible = layer.getVisible();
    if (layerVisible) {
      mapcomponent.map.disableLayer(layer.getProperties().title);
    } else {
      mapcomponent.map.enableLayer(layer.getProperties().title);
    }

    // to reflect the UI visible/invisible state
    if (layers) {
      setOverlayLayers(layers);
    }
  };

  const showOverlaySelectorIcon = () => {
    if (!clientConfig?.map?.overlay_layers) return false;
    return clientConfig?.map?.overlay_layers?.length > 0;
  };

  return (
    <Box
      className="onboarding-mapBox"
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        transition: " background-color 0.2s",
        borderRadius: "6px",
      }}
    >
      <Box
        sx={{
          width: mapExpand ? "100%" : "0px",
          position: "relative",
          overflow: "hidden",
          height: mapExpand ? "100%" : "0px",
          transition: "width 0.3s , height 0.3s",
          borderRadius: "5px",
          backgroundColor: "darkblue.main",
        }}
      >
        <Box
          id="infra3d-container-2"
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
          }}
        >
          {/* Map gets rendered here */}
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "grid",
            gap: 0.5,
            justifyItems: "end",
          }}
        >
          {!showPCLExport && (
            <Tooltip
              title={
                showStreetMap
                  ? t("tooltip.maximize_view")
                  : t("tooltip.maximize_map")
              }
              placement="left"
              enterDelay={400}
              classes={{
                tooltip: "infra3d-buttonDescription",
              }}
            >
              <IconButton
                onClick={() =>
                  monitor(apm, "change-scene-map | Map", () => {
                    changeShowStreetMap(!showStreetMap);
                  })
                }
                size="small"
                className="zoominout-button"
                disableRipple={true}
                sx={{
                  backgroundColor: "darkblue.main",
                  color: "white",
                  "@media (hover: hover)": {
                    ":hover": { color: "secondary.main" },
                  },
                }}
              >
                <ZoomOutMap />
              </IconButton>
            </Tooltip>
          )}
          {showBasemapSelectorIcon() && (
            <Tooltip
              title={t("tooltip.open_basemap_selector")}
              placement="left"
              enterDelay={400}
              classes={{
                tooltip: "infra3d-buttonDescription",
              }}
            >
              <IconButton
                id="infra3d-basemap-button"
                onClick={handleOpenBasemapSelector}
                className="map-button"
                disableRipple={true}
                size="small"
                sx={{
                  backgroundColor: "darkblue.main",
                  color: "white",
                  ":hover": { color: "secondary.main" },
                }}
              >
                {mapSelectorOpen ? <Map /> : <MapOutlined />}
              </IconButton>
            </Tooltip>
          )}
          {showOverlaySelectorIcon() && (
            <Tooltip
              title={t("tooltip.open_overlay_selector")}
              placement="left"
              enterDelay={400}
              classes={{
                tooltip: "infra3d-buttonDescription",
              }}
            >
              <IconButton
                onClick={handleOpenOverlaySelector}
                className="map-button"
                disableRipple={true}
                size="small"
                sx={{
                  backgroundColor: "darkblue.main",
                  color: "white",
                  ":hover": { color: "secondary.main" },
                }}
              >
                {overlaySelectorOpen ? <Texture /> : <TextureOutlined />}
              </IconButton>
            </Tooltip>
          )}
          {mapSelectorOpen && (
            <ClickAwayListener onClickAway={() => setMapSelectorOpen(false)}>
              <Box
                sx={{
                  backgroundColor: "whiteBackground.main",
                  padding: 1,
                  borderRadius: "10px",
                  position: "absolute",
                  top: 0,
                  right: "42px",
                  gap: 1,
                  display: "grid",
                  maxHeight: "378px",
                  overflow: "auto",
                  alignContent: "start",
                }}
              >
                {baseLayers.length > 0 &&
                  baseLayers.map((layer, index) => {
                    const layerTitle = layer.getProperties().title;
                    const thumbnail = layer.getProperties().icon;
                    return (
                      <Tooltip
                        key={index}
                        title={layerTitle}
                        placement="left"
                        enterDelay={400}
                        classes={{
                          tooltip: "infra3d-buttonDescription",
                        }}
                      >
                        <Box
                          sx={{
                            cursor: "pointer",
                            backgroundColor: layer.getVisible()
                              ? "darkblue.main"
                              : "white",
                            padding: 1,
                            "&:hover": {
                              backgroundColor: layer.getVisible()
                                ? "darkblue.main"
                                : "secondary.main",
                            },
                            borderRadius: "8px",
                            height: "80px",
                            width: "140px",
                          }}
                          onClick={() => {
                            handleBasemapSelectorClick(layerTitle);
                          }}
                        >
                          {thumbnail ? (
                            <img
                              style={{
                                height: "100%",
                                width: "100%",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                              src={thumbnail}
                            />
                          ) : (
                            <img
                              style={{
                                height: "100%",
                                width: "100%",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                              src={basemapDefaultThumbnail}
                            />
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
              </Box>
            </ClickAwayListener>
          )}
          {overlaySelectorOpen && (
            <ClickAwayListener
              onClickAway={() => setOverlaySelectorOpen(false)}
            >
              <Box
                sx={{
                  backgroundColor: "whiteBackground.main",
                  padding: 1,
                  borderRadius: "10px",
                  position: "absolute",
                  top: 0,
                  right: "42px",
                  gap: 1,
                  display: "grid",
                  maxHeight: "378px",
                  overflow: "auto",
                  alignContent: "start",
                }}
              >
                {overlayLayers.length > 0 &&
                  overlayLayers.map((layer, index) => {
                    const layerTitle = layer.getProperties().title;
                    const thumbnail = layer.getProperties().icon;
                    return (
                      <Tooltip
                        key={index}
                        title={layerTitle}
                        placement="left"
                        enterDelay={400}
                        classes={{
                          tooltip: "infra3d-buttonDescription",
                        }}
                      >
                        <Box
                          sx={{
                            cursor: "pointer",
                            backgroundColor: layer.getVisible()
                              ? "darkblue.main"
                              : "white",
                            padding: 1,
                            "&:hover": {
                              backgroundColor: layer.getVisible()
                                ? "darkblue.main"
                                : "secondary.main",
                            },
                            borderRadius: "8px",
                            height: "80px",
                            width: "140px",
                          }}
                          onClick={() => {
                            handleOverlaySelectorClick(layerTitle);
                          }}
                        >
                          {thumbnail ? (
                            <img
                              style={{
                                height: "100%",
                                width: "100%",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                              src={thumbnail}
                            />
                          ) : (
                            <img
                              style={{
                                height: "100%",
                                width: "100%",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                              src={basemapDefaultThumbnail}
                            />
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
              </Box>
            </ClickAwayListener>
          )}
        </Box>
        <AttributionButton />
      </Box>
    </Box>
  );
}
